import { ApartmentOutlined, FileTextOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { SectorMultiSelect } from '@components/_SiteWide/HeaderControls'
import { ItemTypes } from '@constants/orderDispatch'
import { ContentTabs, GraviButton, GraviGrid, MaterialIcon } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useLockedOrders } from '@hooks/useLockedOrders'
import { useQueryClient } from '@tanstack/react-query'
import { Select, Switch, Tooltip } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'

import { orderViewColumns } from '@/pages/OrderManagement/components/Grid/Columns/OrderColumnCellrenderers'
import orderRequestIcon from '@assets/CustomIcons/order-request-icon-gradient.svg'
import { useAcceptedOrdersContext } from '../Contexts/AcceptedOrdersContext'
import { useScheduleContext } from '../Contexts/ScheduleContext'
import { useOrderDispatchRowPinning } from '../OrderDispatch'
import { OrderDragOverlay } from './OrderDragOverlay'
import { getExtraOrderAttributes, getOrderDispatchColumnDefs } from './OrderRow'
import { deleteGridView, getGridViewManager, gridViewManager, saveGridView } from '@utils/grid'

type ContentTabKeys = 'all' | 'acceptedOrders' | 'requests'

const STORAGE_KEY = 'order-dispatch-grid'

export function OrdersGrid({
  ordersGridRef,
  triggerModal,
  triggerTPDModal,
  setOrderHistoryStoreId,
  auditOrderId,
  setAuditOrderId,
  config,
  setRequestedOrderId,
  autoAcceptOrderRequest,
  cancelOrderRequest,
  setSelectedRequestedOrder,
}) {
  const { rowPinningApiRef } = useOrderDispatchRowPinning()
  const { removeOrderFromTrip } = useScheduleContext()

  const {
    orders: allOrders,
    handleSectorChange,
    selectedPriceType,
    handlePriceTypeChange,
    metadata,
    onlySplitCandidates,
    setOnlySplitCandidates,
    selectedSectors,
    updateOrInsertOrder,
    isManualPO,
    isRowDragEnabled,
    setIsRowDragEnabled,
    sectors,
  } = useAcceptedOrdersContext()

  // Because of the insane way that ag-grid handles drop zones, we need to keep the locked orders available at the row level.
  // Without this hack, the latest lockedOrders list is never available to the event handlers in the drop zone params (js closure bs)
  // Lucky for us, the handlers have access to the row node and all of it's data, so we can keep the list synced that way.
  // Any other dynamic state needed in the event handlers should be shared the same way here.
  // ------------------------------------------------
  const { lockedOrders } = useLockedOrders()
  const [selectedTabKey, setSelectedTabKey] = useState<ContentTabKeys>('all')
  const [ordersToPin, setOrdersToPin] = useState([])
  const columnApiRef = useRef(null)
  const queryClient = useQueryClient()

  const rowData = useMemo(() => {
    const data = (allOrders ?? []).map((o: Gravitate.Order) => ({ ...o, lockedOrders }))
    const requests = data.filter((o) => o.type.startsWith('Order Request (Requested)'))
    const acceptedOrders = data.filter((o) => !o.type.startsWith('Order Request (Requested'))

    const newOrders = data.filter((o) => o.is_new_order)
    if (newOrders.length > 0) {
      // make sure we don't pin orders that are already pinned
      const uniqueNewOrders = newOrders.filter((order) => ordersToPin.every((o) => o.number !== order.number))
      setOrdersToPin((prev) => [...prev, ...uniqueNewOrders])
    }
    return { all: data, acceptedOrders, requests }
  }, [allOrders, lockedOrders])

  useEffect(() => {
    // remove any pinned orders that are not order requests
    // when the requests tab is selected
    if (selectedTabKey === 'requests') {
      rowPinningApiRef?.current?.pinnedRows?.forEach((r) => {
        if (!r.type.startsWith('Order Request (Requested')) {
          rowPinningApiRef?.current?.unpinRow(r.number)
        }
      })
      return
    }

    if (!ordersToPin?.length) return

    // Pin any new orders that are not already pinned
    ordersToPin.forEach((r) => {
      rowPinningApiRef?.current?.pinRow(r.number)
      queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (cache) => {
        return {
          ...cache,
          rows: cache?.rows?.map((o) => {
            if (o.number !== r.number) return o
            return { ...o, is_new_order: false }
          }),
        }
      })
    })
    setOrdersToPin([])
  }, [ordersToPin, selectedTabKey, queryClient])

  // Fixes an overflow issue specific to the order dispatch page.
  // When dragging an order from the top grid on to a driver, the
  // drag icon can push the page down and introduce a scrollbar with
  // horizontal layout shift.
  useEffect(() => {
    document.body.style.overflowY = 'hidden'
    return () => {
      document.body.style.overflowY = 'auto'
    }
  }, [])

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.TRIPGRIDCELL,
    drop: async (item) => {
      const resp = await removeOrderFromTrip(
        {
          driver_schedule_id: item.scheduleId,
          slot_index: item.tripIndex,
          version: item.version,
        },
        item?.setLoading
      )

      // If the order was succesfully removed from the trip, optimistically add the order back to the open orders list
      if (resp && resp.add_orders?.length > 0) {
        updateOrInsertOrder({ ...item?.order, ...resp.add_orders[0] })
        setOrdersToPin((prev) => [...prev, item?.order])
      }
    },
    collect: (mon) => ({
      isOver: !!mon.isOver(),
      canDrop: !!mon.canDrop(),
    }),
  })

  const { scopes } = useSecurityContext()
  const { updateSetting } = useUserSettings()
  const showCreateOrderButton = scopes?.create_order
  const filterToSplitCandidates = (checked) => {
    setOnlySplitCandidates(checked)
  }

  const [isPoPopoverVisible, setIsPopoverVisible] = useState('')

  const columnDefs = useMemo(() => {
    if (!Object.keys(config).length) return []
    return getOrderDispatchColumnDefs({
      triggerOrderModal: triggerModal,
      triggerThirdPartyDispatchModal: triggerTPDModal,
      auditOrderId,
      lockedOrders,
      scopes,
      setAuditOrderId,
      setOrderHistoryStoreId,
      updateOrder: updateOrInsertOrder,
      isPoPopoverVisible,
      setIsPopoverVisible,
      isManualPO,
      metadata,
      multiTruckEnabled: config.multi_truck || false,
      autoTrailerSwapEnabled: config.auto_trailer_swap || false,
      setRequestedOrderId,
      autoAcceptOrderRequest,
      cancelOrderRequest,
      setSelectedRequestedOrder,
    })
  }, [
    isPoPopoverVisible,
    triggerModal,
    triggerTPDModal,
    auditOrderId,
    lockedOrders,
    scopes,
    config.multi_truck,
    config.auto_trailer_swap,
    isManualPO,
    metadata,
    setRequestedOrderId,
    autoAcceptOrderRequest,
    cancelOrderRequest,
  ])

  const agPropOverrides = useMemo(
    () => ({
      onCellMouseOver: (params) => {
        if (!params.event.buttons) {
          setIsRowDragEnabled(
            !lockedOrders?.map((o) => o.order_id).includes(params.data?.id) &&
              params.data.type !== 'Order Request (Requested)'
          )
        }
      },
      getRowId: (params) => params?.data?.number,
      getRowHeight: () => 40,
      rowDragEntireRow: true,
      suppressMultiRangeSelection: true,
      suppressCopySingleCellRanges: true,
      suppressMoveWhenRowDragging: true,
      frameworkComponents: orderViewColumns,
      headerHeight: 35,
      getRowClass: (params) => {
        if (params.node.group) return ''
        const attrs = getExtraOrderAttributes(params?.data, lockedOrders, scopes)
        return attrs.recentlyEdited ? 'recent-edit' : ''
      },
      defaultColDef: {
        autoHeight: true,
        wrapText: true,
      },
    }),
    [lockedOrders, scopes, isRowDragEnabled]
  )

  const toggleAcceptByColumn = (show: boolean) => {
    if (columnApiRef.current) {
      columnApiRef.current.setColumnsVisible(['accept_by'], show)
    }
  }

  function handleTabKeyChange(key: ContentTabKeys) {
    setSelectedTabKey(key)
    if (key === 'acceptedOrders') {
      toggleAcceptByColumn(false)
    } else {
      toggleAcceptByColumn(true)
    }
  }

  if (!config) return null

  return (
    <div
      style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}
      ref={drop}
      className='order-dispatch-grid-wrapper'
    >
      <ContentTabs
        activeKey={selectedTabKey}
        onChange={handleTabKeyChange}
        items={[
          { title: 'All', key: 'all', icon: <MaterialIcon icon='list' />, count: rowData.all.length },
          { title: 'Orders', key: 'acceptedOrders', icon: <FileTextOutlined />, count: rowData.acceptedOrders.length },
          {
            title: 'Requests',
            key: 'requests',
            icon: <img src={orderRequestIcon} alt='order request icon' style={{ width: '20px' }} className='mr-2' />,
            count: rowData.requests.length,
          },
        ]}
      />
      <OrderDragOverlay isOver={isOver} canDrop={canDrop} />
      <GraviGrid
        enableRowPinning
        externalRef={ordersGridRef}
        columnApiRef={columnApiRef}
        rowPinningRef={rowPinningApiRef}
        storageKey={STORAGE_KEY}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
        onGridConfigChanged={(config) => {
          updateSetting(`gridConfig::${STORAGE_KEY}`, config)
        }}
        primaryKey='number'
        controlBarProps={{
          title: 'Open Orders',
          actionButtons: (
            <>
              <Tooltip trigger='hover' title='Filter To Split Candidates'>
                <Switch
                  checked={onlySplitCandidates}
                  onChange={filterToSplitCandidates}
                  checkedChildren={<ApartmentOutlined />}
                  unCheckedChildren={<ApartmentOutlined />}
                  defaultChecked={false}
                />
              </Tooltip>

              <div className='flex-2'>
                <SectorMultiSelect
                  selectedValues={selectedSectors}
                  sectors={sectors}
                  style={{ width: '100%' }}
                  handleSectorChange={handleSectorChange}
                />
              </div>
              <div className='flex-1'>
                <Select
                  onChange={handlePriceTypeChange}
                  defaultValue={selectedPriceType || undefined}
                  style={{ width: '100%' }}
                  allowClear
                  placeholder='All Price Types'
                >
                  {metadata?.price_types &&
                    metadata.price_types.map((p, i) => (
                      <Select.Option key={i} value={p}>
                        {p}
                      </Select.Option>
                    ))}
                </Select>
              </div>
              {showCreateOrderButton && (
                <GraviButton
                  theme2
                  icon={<PlusCircleOutlined />}
                  buttonText='Create Order'
                  onClick={() => triggerModal()}
                  data-cy='dispatch-create-order-btn'
                />
              )}
            </>
          ),
        }}
        agPropOverrides={agPropOverrides}
        loading={!allOrders}
        columnDefs={columnDefs}
        rowData={rowData[selectedTabKey]}
        enableRangeSelection={false}
      />
    </div>
  )
}
